import { Form, useFormikContext } from 'formik'
import { get, isEmpty } from 'lodash'
import { useContext, useMemo, useState } from 'react'
import {
  FaCheck,
  FaFileDownload,
  FaFileUpload,
  FaPlus,
  FaTrash,
} from 'react-icons/fa'
import { useMutation } from 'react-query'
import { Box, Button, Flex, IconButton, Link, Text } from 'theme-ui'
import { match, when, __ } from 'ts-pattern'
import { useDebouncedCallback } from 'use-debounce'
import { uploadFile } from '../../../api/reportFunction'
import { Table } from '../../../components'
import { InputComponent } from '../../../components/Input/Input'
import InputFile from '../../../components/Input/InputFile'
import { TextareaComponent } from '../../../components/Input/Textarea'
import { notifyErrorAPI } from '../../../components/Notif'
import { PanelComponent } from '../../../components/Panel/Panel'
import { LabeledSelect } from '../../../components/Select/LabeledSelect'
import useCurrentUserStore from '../../../stores/currentUser'
import { matchRegExp, newUuid } from '../../../utils'
import AddSuspectsModal from './AddSuspectsModal'
import { ReportContext, ReportFormValues } from './ReportForm'

function InvolvedParties() {
  const context = useContext(ReportContext)
  const roles = useCurrentUserStore((s) => s.currentUser?.data?.roles)
  const [state, setState] = useState({
    namaPersonel: '',
    unitDivisi: '',
  })
  const { values, setFieldValue, errors } = useFormikContext<ReportFormValues>()
  const involvedParties = values.involvedParties

  function addParty(e) {
    e.preventDefault()
    e.stopPropagation()

    setFieldValue('involvedParties.valueName', [
      ...involvedParties?.valueName,
      { ...state, id: newUuid() },
    ])
    setState({ unitDivisi: '', namaPersonel: '' })
  }

  // const provenColumnVisible = isInvestigating || penalyColumnsVisible
  // const provenColumnVisible = match()
  // const isInvestigating =
  //   context.report?.state === 'reviewed' &&
  //   ['admin_investigator', 'superadmin'].includes(roles)
  const [provenColumnVisible, canChangeSuspects, penaltyColumnVisible] = match([
    roles,
    context.report?.state,
    context.report?.violation,
  ])
    .with(
      [when(matchRegExp(/admin_investigator|superadmin/)), 'reviewed', __],
      () => [true, true, false]
    )
    .with(
      [
        when(matchRegExp(/admin_reviewer|admin_board_of_honor/)),
        'verdict',
        true,
      ],
      () => [true, false, true]
    )
    .with([when(matchRegExp(/admin/)), 'verdict', true], () => [
      true,
      false,
      false,
    ])
    .with(['user', __, __], () => [true, false, false])
    .otherwise(() => [false, false, false])

  return (
    <PanelComponent
      id="involvedParties"
      header="Siapa Saja Yang Terlibat?"
      variant={errors.involvedParties?.valueName ? 'error' : 'primary'}
      childPadding={[1, null, null]}
    >
      <Text color="error">{errors.involvedParties?.valueName}</Text>
      {context.state === 'editing' && (
        <Form onSubmitCapture={addParty}>
          <Flex sx={{ alignItems: 'end', gap: 2 }}>
            <Box sx={{ flex: 2 }}>
              <InputComponent
                required
                label="Nama Personel"
                onChange={(e) =>
                  setState((prev) => ({
                    ...prev,
                    namaPersonel: e.target.value,
                  }))
                }
                value={state.namaPersonel}
              />
            </Box>
            <Box sx={{ flex: 2 }}>
              <LabeledSelect
                required
                label="Unit Kerja"
                onChange={(e) =>
                  setState((prev) => ({ ...prev, unitDivisi: e.target.value }))
                }
                value={state.unitDivisi}
              >
                <option value=""></option>
                {context.listDivisi.map((division) => (
                  <option key={division.code} value={division.code}>
                    {division.value}
                  </option>
                ))}
              </LabeledSelect>
            </Box>
            <Box sx={{ flex: 1, textAlign: 'right' }}>
              <Button type="submit">
                <Box as={FaPlus} pt="1" />{' '}
                <Box as="span" sx={{ display: ['none', 'inline'] }}>
                  Tambah
                </Box>
              </Button>
            </Box>
          </Flex>
        </Form>
      )}

      {!isEmpty(involvedParties.valueName) && (
        <Table
          disablePagination
          options={{
            columns: [
              { Header: 'No', width: '5%', Cell: ({ row }) => row.index + 1 },
              { Header: 'Nama Personel', accessor: 'namaPersonel' },
              { Header: 'Unit Kerja', accessor: 'unitDivisi', width: '15%' },
              ...(provenColumnVisible
                ? [
                    {
                      Header: 'Terbukti bersalah',
                      Cell: ProvenCell,
                      width: '7%',
                    },
                  ]
                : []),
              ...(context.state === 'editing'
                ? [{ Header: 'Aksi', Cell: ActionCell, width: '10%' }]
                : []),
              ...(penaltyColumnVisible
                ? [
                    { Header: 'Sanksi', Cell: PenaltyCell },
                    { Header: 'Dokumen Sanksi', Cell: PenaltyDocumentCell },
                  ]
                : []),
            ],
            data: involvedParties.valueName,
          }}
        />
      )}

      {canChangeSuspects && (
        <AddSuspectsModal
          reportId={context.report.id}
          currentSuspects={involvedParties.valueName}
        />
      )}
    </PanelComponent>
  )
}

function ActionCell(props) {
  const id = props.row.original.id
  const { setFieldValue } = useFormikContext()

  const handleDelete = () => {
    const parties = props.data?.filter((data) => data.id !== id)
    setFieldValue('involvedParties.valueName', parties)
  }

  return (
    <IconButton onClick={handleDelete}>
      <FaTrash size={45} />
    </IconButton>
  )
}

function ProvenCell(props) {
  const index = props.row.index
  const checked = props.data[index].proven
  if (!checked) return null
  return <FaCheck color="green" />
}

function PenaltyCell({ row }) {
  const { setFieldValue, values } = useFormikContext<ReportFormValues>()

  const field = `involvedParties.valueName.${row.index}.penalty`
  const value = useMemo(() => get(values, field, ''), [field, values])
  const update = useDebouncedCallback((field, val) => {
    setFieldValue(field, val)
  }, 800)

  if (!row.original.proven) return null

  return (
    <Box sx={{ position: 'relative' }}>
      <TextareaComponent
        onChange={(e) => update(field, e.target.value)}
        defaultValue={value}
        sx={{ height: '40px', fontSize: '0.6rem', resize: 'vertical' }}
      />
    </Box>
  )
}

function PenaltyDocumentCell({ row }) {
  const inputId = `penalty_${row.id}`
  const { filePath } = row.original
  const { values, setFieldValue } = useFormikContext<ReportFormValues>()
  const uploadM = useMutation((values: any) => uploadFile(values), {
    onError: notifyErrorAPI,
  })

  function handleUpload({ base64, e }) {
    const file = e.target.files[0]
    uploadM
      .mutateAsync({
        attachment: {
          fileName: file.name,
          file: base64,
        },
      })
      .then((response) => {
        const { urlPath, fileName } = response?.data?.data || {}
        const suspects = values.involvedParties.valueName.map((suspect, i) => {
          if (i === row.index)
            return { ...suspect, filePath: urlPath, fileName }
          return suspect
        })

        setFieldValue('involvedParties.valueName', suspects)
      })
  }

  if (!row.original.proven) return null

  return (
    <>
      {filePath && (
        <Link href={filePath} download={filePath} target="_blank">
          Unduh <Box as={FaFileDownload} mb={-1} />
        </Link>
      )}
      <Button
        variant="link"
        onClick={() => {
          document.getElementById(inputId).click()
        }}
      >
        {filePath ? 'Ganti' : 'Upload'} <Box as={FaFileUpload} mb={-1} />
      </Button>
      <Box sx={{ display: 'none' }}>
        <InputFile
          id={inputId}
          onChange={handleUpload}
          loading={uploadM.isLoading}
        />
      </Box>
    </>
  )
}

export default InvolvedParties
